import {
    Badge,
    Button,
    Card,
    Collapse,
    Divider,
    Group,
    Loader,
    Modal,
    Pagination,
    Table,
    Text,
    TextInput,
} from "@mantine/core";
import {
    IconAlertCircle,
    IconBuildingWarehouse,
    IconChevronDown,
    IconChevronUp,
    IconClock,
    IconPackage,
    IconRefresh,
    IconTruck,
    IconUser,
    IconUsers,
} from "@tabler/icons-react";
import React, {
    useCallback,
    useEffect,
    useState,
} from "react";
import { useSelector } from "react-redux";

import {
    FloorAllocationDetail,
    FloorUserStatsResponse,
    ForkliftAllocationDetail,
    ForkliftDriverStatsResponse,
    ForkliftUser,
    OperationsStatsResponse,
    OrderActivity,
    OrderActivityResponse,
    UserSummary,
    getFloorUserStats,
    getForkliftDriverStats,
    getOperationsStats,
    getOrderActivityStats,
} from "../../api/forkliftStats";

import NavBar from "../../components/NavBar/NavBar";
import classes from "./ForkliftDashboard.module.css";

const PAGE_SIZE = 25;

type PersonType =
    | "forklift"
    | "floor"
    | null;

interface SelectedPerson {
    id: string;
    type: PersonType;
    name: string;
}

/* =========================================================
   HELPERS
========================================================= */

const formatDateTime = (
    value?: string | null
) => {
    if (!value) {
        return "-";
    }

    return new Date(value).toLocaleString(
        "en-CA",
        {
            year: "numeric",
            month: "short",
            day: "numeric",
            hour: "numeric",
            minute: "2-digit",
        }
    );
};

const formatDuration = (
    seconds?: number | null
) => {
    if (
        seconds === null ||
        seconds === undefined
    ) {
        return "-";
    }

    if (seconds < 60) {
        return `${seconds}s`;
    }

    const minutes =
        Math.floor(seconds / 60);

    const remainingSeconds =
        seconds % 60;

    if (minutes < 60) {
        return remainingSeconds
            ? `${minutes}m ${remainingSeconds}s`
            : `${minutes}m`;
    }

    const hours =
        Math.floor(minutes / 60);

    const remainingMinutes =
        minutes % 60;

    return remainingMinutes
        ? `${hours}h ${remainingMinutes}m`
        : `${hours}h`;
};

const formatMinutes = (
    value?: number | string | null
) => {
    if (
        value === null ||
        value === undefined ||
        value === ""
    ) {
        return "-";
    }

    const minutes =
        Number(value);

    if (
        Number.isNaN(minutes)
    ) {
        return "-";
    }

    if (minutes < 60) {
        return `${minutes.toFixed(1)} min`;
    }

    const hours =
        Math.floor(minutes / 60);

    const remaining =
        Math.round(minutes % 60);

    return remaining
        ? `${hours}h ${remaining}m`
        : `${hours}h`;
};

const formatStatus = (
    status?: string | null
) => {
    if (!status) {
        return "Pending";
    }

    return status
        .replace(/_/g, " ")
        .replace(
            /\w\S*/g,
            (word) =>
                word.charAt(0).toUpperCase() +
                word
                    .slice(1)
                    .toLowerCase()
        );
};

const getStatusColor = (
    status?: string | null
) => {
    const normalized =
        status
            ?.toLowerCase()
            .trim() || "";

    if (
        normalized === "picked" ||
        normalized === "picked_all"
    ) {
        return "green";
    }

    if (
        normalized === "not_found"
    ) {
        return "red";
    }

    if (
        normalized === "allocation"
    ) {
        return "blue";
    }

    return "gray";
};

/* =========================================================
   COMPONENT
========================================================= */

const ForkliftDashboard: React.FC = () => {
    const token = useSelector(
        (state: any) =>
            state.user.sessionId
    );

    /* =====================================================
       OVERVIEW
    ===================================================== */

    const [stats, setStats] =
        useState<OperationsStatsResponse | null>(
            null
        );

    const [loading, setLoading] =
        useState(false);

    /* =====================================================
       ORDER ACTIVITY
    ===================================================== */

    const [
        orderActivity,
        setOrderActivity,
    ] =
        useState<OrderActivityResponse | null>(
            null
        );

    const [
        orderLoading,
        setOrderLoading,
    ] = useState(false);

    const [
        orderPage,
        setOrderPage,
    ] = useState(1);

    const [
        orderFrom,
        setOrderFrom,
    ] = useState("");

    const [
        orderTo,
        setOrderTo,
    ] = useState("");

    const [
        expandedOrders,
        setExpandedOrders,
    ] = useState<number[]>([]);

    /* =====================================================
       PERSON DETAIL
    ===================================================== */

    const [
        selectedPerson,
        setSelectedPerson,
    ] =
        useState<SelectedPerson | null>(
            null
        );

    const [
        detailLoading,
        setDetailLoading,
    ] = useState(false);

    const [
        forkliftDetail,
        setForkliftDetail,
    ] =
        useState<ForkliftDriverStatsResponse | null>(
            null
        );

    const [
        floorDetail,
        setFloorDetail,
    ] =
        useState<FloorUserStatsResponse | null>(
            null
        );

    const [
        detailPage,
        setDetailPage,
    ] = useState(1);

    const [
        completedFrom,
        setCompletedFrom,
    ] = useState("");

    const [
        completedTo,
        setCompletedTo,
    ] = useState("");

    /* =====================================================
       LOAD OVERVIEW
    ===================================================== */

    const loadStats =
        useCallback(async () => {
            if (!token) {
                return;
            }

            try {
                setLoading(true);

                const response =
                    await getOperationsStats(
                        token
                    );

                setStats(response);
            } catch (error) {
                console.error(
                    "Unable to load operations dashboard.",
                    error
                );

                alert(
                    "Unable to load operations dashboard."
                );
            } finally {
                setLoading(false);
            }
        }, [token]);

    useEffect(() => {
        loadStats();
    }, [loadStats]);

    /* =====================================================
       LOAD ORDER ACTIVITY
    ===================================================== */

    const loadOrderActivity =
        useCallback(async () => {
            if (!token) {
                return;
            }

            if (
                orderFrom &&
                orderTo &&
                orderFrom > orderTo
            ) {
                alert(
                    "From date cannot be after To date."
                );

                return;
            }

            try {
                setOrderLoading(
                    true
                );

                const response =
                    await getOrderActivityStats(
                        token,
                        {
                            page:
                                orderPage,

                            size:
                                PAGE_SIZE,

                            from:
                                orderFrom,

                            to:
                                orderTo,
                        }
                    );

                setOrderActivity(
                    response
                );
            } catch (error) {
                console.error(
                    "Unable to load order activity.",
                    error
                );

                alert(
                    "Unable to load order activity."
                );
            } finally {
                setOrderLoading(
                    false
                );
            }
        }, [
            token,
            orderPage,
            orderFrom,
            orderTo,
        ]);

    useEffect(() => {
        loadOrderActivity();
    }, [
        loadOrderActivity,
        orderPage,
    ]);

    const applyOrderFilters = () => {
        if (
            orderFrom &&
            orderTo &&
            orderFrom > orderTo
        ) {
            alert(
                "From date cannot be after To date."
            );

            return;
        }

        if (orderPage !== 1) {
            setOrderPage(1);

            return;
        }

        loadOrderActivity();
    };

    const clearOrderFilters = () => {
        setOrderFrom("");
        setOrderTo("");
        setExpandedOrders([]);

        if (orderPage !== 1) {
            setOrderPage(1);

            return;
        }

        setTimeout(() => {
            loadOrderActivity();
        }, 0);
    };

    const toggleOrder = (
        poId: number
    ) => {
        setExpandedOrders(
            (prev) =>
                prev.includes(poId)
                    ? prev.filter(
                        (id) =>
                            id !== poId
                    )
                    : [
                        ...prev,
                        poId,
                    ]
        );
    };

    /* =====================================================
       PERSON DETAIL
    ===================================================== */

    const loadPersonDetail =
        useCallback(async () => {
            if (
                !token ||
                !selectedPerson
            ) {
                return;
            }

            if (
                completedFrom &&
                completedTo &&
                completedFrom >
                completedTo
            ) {
                alert(
                    "Completed From cannot be after Completed To."
                );

                return;
            }

            try {
                setDetailLoading(
                    true
                );

                const params = {
                    page:
                        detailPage,

                    size:
                        PAGE_SIZE,

                    completedFrom,

                    completedTo,
                };

                if (
                    selectedPerson.type ===
                    "forklift"
                ) {
                    const response =
                        await getForkliftDriverStats(
                            token,
                            selectedPerson.id,
                            params
                        );

                    setForkliftDetail(
                        response
                    );

                    setFloorDetail(
                        null
                    );
                }

                if (
                    selectedPerson.type ===
                    "floor"
                ) {
                    const response =
                        await getFloorUserStats(
                            token,
                            selectedPerson.id,
                            params
                        );

                    setFloorDetail(
                        response
                    );

                    setForkliftDetail(
                        null
                    );
                }
            } catch (error) {
                console.error(
                    "Unable to load person stats.",
                    error
                );

                alert(
                    "Unable to load user statistics."
                );
            } finally {
                setDetailLoading(
                    false
                );
            }
        }, [
            token,
            selectedPerson,
            detailPage,
            completedFrom,
            completedTo,
        ]);

    useEffect(() => {
        if (!selectedPerson) {
            return;
        }

        loadPersonDetail();
    }, [
        selectedPerson,
        detailPage,
        loadPersonDetail,
    ]);

    const openForkliftUser = (
        user: ForkliftUser
    ) => {
        setCompletedFrom("");
        setCompletedTo("");
        setDetailPage(1);

        setForkliftDetail(null);
        setFloorDetail(null);

        setSelectedPerson({
            id: user._id,
            type: "forklift",
            name: user.full_name,
        });
    };

    const openFloorUser = (
        user: UserSummary
    ) => {
        setCompletedFrom("");
        setCompletedTo("");
        setDetailPage(1);

        setForkliftDetail(null);
        setFloorDetail(null);

        setSelectedPerson({
            id: user._id,
            type: "floor",
            name: user.full_name,
        });
    };

    const closePersonModal = () => {
        setSelectedPerson(null);
        setForkliftDetail(null);
        setFloorDetail(null);

        setCompletedFrom("");
        setCompletedTo("");

        setDetailPage(1);
    };

    const applyDetailFilters = () => {
        if (
            completedFrom &&
            completedTo &&
            completedFrom >
            completedTo
        ) {
            alert(
                "Completed From cannot be after Completed To."
            );

            return;
        }

        if (detailPage !== 1) {
            setDetailPage(1);

            return;
        }

        loadPersonDetail();
    };

    const clearDetailFilters = () => {
        setCompletedFrom("");
        setCompletedTo("");

        if (detailPage !== 1) {
            setDetailPage(1);
        }
    };

    /* =====================================================
       LATEST USER ACTIVITY
    ===================================================== */

    const findLatestForklift = (
        userId: string
    ) =>
        stats?.forklift.latestByDriver.find(
            (item) =>
                item.user_id === userId
        );

    const findLatestFloor = (
        userId: string
    ) =>
        stats?.floor.latestByUser.find(
            (item) =>
                item.user_id === userId
        );

    /* =====================================================
       ORDER TABLE
    ===================================================== */

    const renderOrderStyles = (
        order: OrderActivity
    ) => {
        if (!order.styles?.length) {
            return (
                <div
                    className={
                        classes.emptyTable
                    }
                >
                    <Text
                        size="sm"
                        c="dimmed"
                    >
                        No styles found.
                    </Text>
                </div>
            );
        }

        return (
            <div
                className={
                    classes.orderStyleTable
                }
            >
                <Table
                    striped
                    highlightOnHover
                    verticalSpacing="xs"
                >
                    <Table.Thead>
                        <Table.Tr>
                            <Table.Th>
                                Style
                            </Table.Th>

                            <Table.Th>
                                Description
                            </Table.Th>

                            <Table.Th>
                                Driver
                            </Table.Th>

                            <Table.Th>
                                Floor
                            </Table.Th>

                            <Table.Th>
                                Status
                            </Table.Th>

                            <Table.Th>
                                Pick Start
                            </Table.Th>

                            <Table.Th>
                                Pick Complete
                            </Table.Th>

                            <Table.Th>
                                Updated
                            </Table.Th>
                        </Table.Tr>
                    </Table.Thead>

                    <Table.Tbody>
                        {order.styles.map(
                            (style) => (
                                <Table.Tr
                                    key={
                                        style.allocation_id
                                    }
                                >
                                    <Table.Td>
                                        <Text
                                            fw={
                                                700
                                            }
                                            size="sm"
                                        >
                                            {style.style_no ||
                                                "-"}
                                        </Text>
                                    </Table.Td>

                                    <Table.Td>
                                        <Text
                                            size="sm"
                                        >
                                            {style.description ||
                                                "-"}
                                        </Text>
                                    </Table.Td>

                                    <Table.Td>
                                        <Text
                                            size="sm"
                                        >
                                            {style.driver ||
                                                "-"}
                                        </Text>
                                    </Table.Td>

                                    <Table.Td>
                                        <Text
                                            size="sm"
                                        >
                                            {style.lead ||
                                                "-"}
                                        </Text>
                                    </Table.Td>

                                    <Table.Td>
                                        <Badge
                                            variant="light"
                                            color={getStatusColor(
                                                style.order_status
                                            )}
                                        >
                                            {formatStatus(
                                                style.order_status
                                            )}
                                        </Badge>
                                    </Table.Td>

                                    <Table.Td>
                                        <Text
                                            size="sm"
                                        >
                                            {formatDateTime(
                                                style.pick_started_at
                                            )}
                                        </Text>
                                    </Table.Td>

                                    <Table.Td>
                                        <Text
                                            size="sm"
                                        >
                                            {formatDateTime(
                                                style.pick_completed_at
                                            )}
                                        </Text>
                                    </Table.Td>

                                    <Table.Td>
                                        <Text
                                            size="sm"
                                            c="dimmed"
                                        >
                                            {formatDateTime(
                                                style.updatedAt
                                            )}
                                        </Text>
                                    </Table.Td>
                                </Table.Tr>
                            )
                        )}
                    </Table.Tbody>
                </Table>
            </div>
        );
    };

    /* =====================================================
       FORKLIFT DETAIL TABLE
    ===================================================== */

    const renderForkliftDetailTable = (
        data: ForkliftAllocationDetail[]
    ) => {
        if (!data.length) {
            return (
                <div
                    className={
                        classes.emptyTable
                    }
                >
                    <Text
                        size="sm"
                        c="dimmed"
                    >
                        No forklift allocations
                        found.
                    </Text>
                </div>
            );
        }

        return (
            <div
                className={
                    classes.tableWrapper
                }
            >
                <Table
                    striped
                    highlightOnHover
                    withTableBorder
                    verticalSpacing="sm"
                >
                    <Table.Thead>
                        <Table.Tr>
                            <Table.Th>
                                PO
                            </Table.Th>

                            <Table.Th>
                                Style
                            </Table.Th>

                            <Table.Th>
                                Status
                            </Table.Th>

                            <Table.Th>
                                Started
                            </Table.Th>

                            <Table.Th>
                                Completed
                            </Table.Th>

                            <Table.Th>
                                Duration
                            </Table.Th>
                        </Table.Tr>
                    </Table.Thead>

                    <Table.Tbody>
                        {data.map(
                            (item) => (
                                <Table.Tr
                                    key={
                                        item.allocation_id
                                    }
                                >
                                    <Table.Td>
                                        <Text
                                            fw={
                                                600
                                            }
                                            size="sm"
                                        >
                                            {item.po_no ||
                                                "-"}
                                        </Text>
                                    </Table.Td>

                                    <Table.Td>
                                        <div>
                                            <Text
                                                fw={
                                                    700
                                                }
                                                size="sm"
                                            >
                                                {item.style_no ||
                                                    "-"}
                                            </Text>

                                            {item.description && (
                                                <Text
                                                    size="xs"
                                                    c="dimmed"
                                                >
                                                    {
                                                        item.description
                                                    }
                                                </Text>
                                            )}
                                        </div>
                                    </Table.Td>

                                    <Table.Td>
                                        <Badge
                                            variant="light"
                                            color={getStatusColor(
                                                item.order_status
                                            )}
                                        >
                                            {formatStatus(
                                                item.order_status
                                            )}
                                        </Badge>
                                    </Table.Td>

                                    <Table.Td>
                                        <Text size="sm">
                                            {formatDateTime(
                                                item.pick_started_at
                                            )}
                                        </Text>
                                    </Table.Td>

                                    <Table.Td>
                                        <Text size="sm">
                                            {formatDateTime(
                                                item.pick_completed_at
                                            )}
                                        </Text>
                                    </Table.Td>

                                    <Table.Td>
                                        <Badge
                                            variant="light"
                                            color="blue"
                                        >
                                            {formatDuration(
                                                item.pick_duration_seconds
                                            )}
                                        </Badge>
                                    </Table.Td>
                                </Table.Tr>
                            )
                        )}
                    </Table.Tbody>
                </Table>
            </div>
        );
    };

    /* =====================================================
       FLOOR DETAIL TABLE
    ===================================================== */

    const renderFloorDetailTable = (
        data: FloorAllocationDetail[]
    ) => {
        if (!data.length) {
            return (
                <div
                    className={
                        classes.emptyTable
                    }
                >
                    <Text
                        size="sm"
                        c="dimmed"
                    >
                        No floor allocations
                        found.
                    </Text>
                </div>
            );
        }

        return (
            <div
                className={
                    classes.tableWrapper
                }
            >
                <Table
                    striped
                    highlightOnHover
                    withTableBorder
                    verticalSpacing="sm"
                >
                    <Table.Thead>
                        <Table.Tr>
                            <Table.Th>
                                PO
                            </Table.Th>

                            <Table.Th>
                                Style
                            </Table.Th>

                            <Table.Th>
                                Status
                            </Table.Th>

                            <Table.Th>
                                Est. Start
                            </Table.Th>

                            <Table.Th>
                                Completed
                            </Table.Th>

                            <Table.Th>
                                Est. Duration
                            </Table.Th>
                        </Table.Tr>
                    </Table.Thead>

                    <Table.Tbody>
                        {data.map(
                            (item) => (
                                <Table.Tr
                                    key={
                                        item.allocation_id
                                    }
                                >
                                    <Table.Td>
                                        <Text
                                            fw={
                                                600
                                            }
                                            size="sm"
                                        >
                                            {item.po_no ||
                                                "-"}
                                        </Text>
                                    </Table.Td>

                                    <Table.Td>
                                        <div>
                                            <Text
                                                fw={
                                                    700
                                                }
                                                size="sm"
                                            >
                                                {item.style_no ||
                                                    "-"}
                                            </Text>

                                            {item.description && (
                                                <Text
                                                    size="xs"
                                                    c="dimmed"
                                                >
                                                    {
                                                        item.description
                                                    }
                                                </Text>
                                            )}
                                        </div>
                                    </Table.Td>

                                    <Table.Td>
                                        <Badge
                                            variant="light"
                                            color={getStatusColor(
                                                item.order_status
                                            )}
                                        >
                                            {formatStatus(
                                                item.order_status
                                            )}
                                        </Badge>
                                    </Table.Td>

                                    <Table.Td>
                                        <Text size="sm">
                                            {formatDateTime(
                                                item.estimated_started_at
                                            )}
                                        </Text>
                                    </Table.Td>

                                    <Table.Td>
                                        <Text size="sm">
                                            {formatDateTime(
                                                item.completed_at
                                            )}
                                        </Text>
                                    </Table.Td>

                                    <Table.Td>
                                        <Badge
                                            variant="light"
                                            color="violet"
                                        >
                                            {formatDuration(
                                                item.floor_duration_seconds
                                            )}
                                        </Badge>
                                    </Table.Td>
                                </Table.Tr>
                            )
                        )}
                    </Table.Tbody>
                </Table>
            </div>
        );
    };

    /* =====================================================
       UI
    ===================================================== */

    return (
        <div
            className={
                classes.page
            }
        >
            <NavBar />

            <main
                className={
                    classes.main
                }
            >
                {/* HEADER */}

                <header
                    className={
                        classes.header
                    }
                >
                    <div>
                        <Text
                            className={
                                classes.title
                            }
                        >
                            Distribution Center Dashboard
                        </Text>

                        <Text
                            size="sm"
                            c="dimmed"
                        >
                            Forklift picking, floor allocation
                            and order activity.
                        </Text>
                    </div>

                    <Button
                        variant="default"
                        leftSection={
                            <IconRefresh
                                size={17}
                            />
                        }
                        loading={loading}
                        onClick={() => {
                            loadStats();
                            loadOrderActivity();
                        }}
                    >
                        Refresh
                    </Button>
                </header>

                {loading && !stats ? (
                    <div
                        className={
                            classes.loading
                        }
                    >
                        <Loader />

                        <Text
                            c="dimmed"
                        >
                            Loading dashboard...
                        </Text>
                    </div>
                ) : (
                    <>
                        {/* =================================================
                            COMPACT DAILY SUMMARY
                        ================================================= */}

                        <div
                            className={
                                classes.compactOverview
                            }
                        >
                            <section>
                                <Text
                                    className={
                                        classes.compactSectionTitle
                                    }
                                >
                                    Forklift Today
                                </Text>

                                <div
                                    className={
                                        classes.compactStatGrid
                                    }
                                >
                                    <Card
                                        withBorder
                                        className={
                                            classes.compactStatCard
                                        }
                                    >
                                        <IconPackage
                                            size={18}
                                        />

                                        <div>
                                            <Text
                                                size="xs"
                                                c="dimmed"
                                            >
                                                Completed
                                            </Text>

                                            <Text
                                                className={
                                                    classes.compactValue
                                                }
                                            >
                                                {stats?.forklift.summary.completed_today ??
                                                    0}
                                            </Text>
                                        </div>
                                    </Card>

                                    <Card
                                        withBorder
                                        className={
                                            classes.compactStatCard
                                        }
                                    >
                                        <IconTruck
                                            size={18}
                                        />

                                        <div>
                                            <Text
                                                size="xs"
                                                c="dimmed"
                                            >
                                                Drivers
                                            </Text>

                                            <Text
                                                className={
                                                    classes.compactValue
                                                }
                                            >
                                                {stats?.forklift.summary.active_drivers_today ??
                                                    0}
                                            </Text>
                                        </div>
                                    </Card>

                                    <Card
                                        withBorder
                                        className={
                                            classes.compactStatCard
                                        }
                                    >
                                        <IconClock
                                            size={18}
                                        />

                                        <div>
                                            <Text
                                                size="xs"
                                                c="dimmed"
                                            >
                                                Avg Time
                                            </Text>

                                            <Text
                                                className={
                                                    classes.compactValueSmall
                                                }
                                            >
                                                {formatMinutes(
                                                    stats?.forklift.summary.average_pick_minutes_today
                                                )}
                                            </Text>
                                        </div>
                                    </Card>
                                </div>
                            </section>

                            <section>
                                <Text
                                    className={
                                        classes.compactSectionTitle
                                    }
                                >
                                    Floor Today
                                </Text>

                                <div
                                    className={
                                        classes.compactStatGrid
                                    }
                                >
                                    <Card
                                        withBorder
                                        className={
                                            classes.compactStatCard
                                        }
                                    >
                                        <IconPackage
                                            size={18}
                                        />

                                        <div>
                                            <Text
                                                size="xs"
                                                c="dimmed"
                                            >
                                                Styles
                                            </Text>

                                            <Text
                                                className={
                                                    classes.compactValue
                                                }
                                            >
                                                {stats?.floor.summary.completed_styles_today ??
                                                    0}
                                            </Text>
                                        </div>
                                    </Card>

                                    <Card
                                        withBorder
                                        className={
                                            classes.compactStatCard
                                        }
                                    >
                                        <IconBuildingWarehouse
                                            size={18}
                                        />

                                        <div>
                                            <Text
                                                size="xs"
                                                c="dimmed"
                                            >
                                                Stores
                                            </Text>

                                            <Text
                                                className={
                                                    classes.compactValue
                                                }
                                            >
                                                {stats?.floor.summary.stores_completed_today ??
                                                    0}
                                            </Text>
                                        </div>
                                    </Card>

                                    <Card
                                        withBorder
                                        className={
                                            classes.compactStatCard
                                        }
                                    >
                                        <IconUsers
                                            size={18}
                                        />

                                        <div>
                                            <Text
                                                size="xs"
                                                c="dimmed"
                                            >
                                                Users
                                            </Text>

                                            <Text
                                                className={
                                                    classes.compactValue
                                                }
                                            >
                                                {stats?.floor.summary.active_floor_users_today ??
                                                    0}
                                            </Text>
                                        </div>
                                    </Card>

                                    <Card
                                        withBorder
                                        className={
                                            classes.compactStatCard
                                        }
                                    >
                                        <IconClock
                                            size={18}
                                        />

                                        <div>
                                            <Text
                                                size="xs"
                                                c="dimmed"
                                            >
                                                Avg Time
                                            </Text>

                                            <Text
                                                className={
                                                    classes.compactValueSmall
                                                }
                                            >
                                                {formatMinutes(
                                                    stats?.floor.summary.average_floor_minutes_today
                                                )}
                                            </Text>
                                        </div>
                                    </Card>
                                </div>
                            </section>
                        </div>

                        {/* =================================================
                            PENDING COMPACT
                        ================================================= */}

                        <div
                            className={
                                classes.pendingInline
                            }
                        >
                            <div
                                className={
                                    classes.pendingInlineTitle
                                }
                            >
                                <IconAlertCircle
                                    size={18}
                                />

                                <Text fw={700}>
                                    Pending
                                </Text>
                            </div>

                            <div
                                className={
                                    classes.pendingItem
                                }
                            >
                                <span>
                                    Forklift
                                </span>

                                <strong>
                                    {stats?.pending.forklift_pending ??
                                        0}
                                </strong>
                            </div>

                            <div
                                className={
                                    classes.pendingItem
                                }
                            >
                                <span>
                                    New Product Floor
                                </span>

                                <strong>
                                    {stats?.pending.new_product_floor_pending ??
                                        0}
                                </strong>
                            </div>

                            <div
                                className={
                                    classes.pendingItem
                                }
                            >
                                <span>
                                    Restock Floor
                                </span>

                                <strong>
                                    {stats?.pending.restock_floor_pending ??
                                        0}
                                </strong>
                            </div>
                        </div>

                        {/* =================================================
                            ORDER ACTIVITY
                        ================================================= */}

                        <section
                            className={
                                classes.orderSection
                            }
                        >
                            <div
                                className={
                                    classes.sectionHeader
                                }
                            >
                                <div>
                                    <Text
                                        fw={750}
                                        size="lg"
                                    >
                                        Order Activity
                                    </Text>

                                    <Text
                                        size="sm"
                                        c="dimmed"
                                    >
                                        PO activity based on pick
                                        start, pick completion or
                                        latest allocation update.
                                    </Text>
                                </div>

                                <Badge
                                    variant="light"
                                    color="gray"
                                >
                                    {orderActivity
                                        ?.pagination
                                        .totalItems ??
                                        0}{" "}
                                    POs
                                </Badge>
                            </div>

                            <div
                                className={
                                    classes.orderFilters
                                }
                            >
                                <TextInput
                                    type="date"
                                    label="From"
                                    value={
                                        orderFrom
                                    }
                                    onChange={(
                                        e
                                    ) =>
                                        setOrderFrom(
                                            e.currentTarget
                                                .value
                                        )
                                    }
                                />

                                <TextInput
                                    type="date"
                                    label="To"
                                    value={
                                        orderTo
                                    }
                                    onChange={(
                                        e
                                    ) =>
                                        setOrderTo(
                                            e.currentTarget
                                                .value
                                        )
                                    }
                                />

                                <Button
                                    onClick={
                                        applyOrderFilters
                                    }
                                    loading={
                                        orderLoading
                                    }
                                >
                                    Apply
                                </Button>

                                <Button
                                    variant="default"
                                    onClick={
                                        clearOrderFilters
                                    }
                                >
                                    Today
                                </Button>
                            </div>

                            {orderLoading &&
                                !orderActivity ? (
                                <div
                                    className={
                                        classes.sectionLoader
                                    }
                                >
                                    <Loader size="sm" />
                                </div>
                            ) : (
                                <div
                                    className={
                                        classes.orderTableWrapper
                                    }
                                >
                                    <Table
                                        striped
                                        highlightOnHover
                                        verticalSpacing="sm"
                                    >
                                        <Table.Thead>
                                            <Table.Tr>
                                                <Table.Th />

                                                <Table.Th>
                                                    PO
                                                </Table.Th>

                                                <Table.Th>
                                                    Supplier
                                                </Table.Th>

                                                <Table.Th>
                                                    Styles
                                                </Table.Th>

                                                <Table.Th>
                                                    Drivers
                                                </Table.Th>

                                                <Table.Th>
                                                    Floor
                                                </Table.Th>

                                                <Table.Th>
                                                    Started
                                                </Table.Th>

                                                <Table.Th>
                                                    Completed
                                                </Table.Th>

                                                <Table.Th>
                                                    Last Activity
                                                </Table.Th>
                                            </Table.Tr>
                                        </Table.Thead>

                                        <Table.Tbody>
                                            {orderActivity?.orders.map(
                                                (order) => {
                                                    const expanded =
                                                        expandedOrders.includes(
                                                            order.po_id
                                                        );

                                                    return (
                                                        <React.Fragment
                                                            key={
                                                                order.po_id
                                                            }
                                                        >
                                                            <Table.Tr>
                                                                <Table.Td>
                                                                    <Button
                                                                        variant="subtle"
                                                                        size="compact-sm"
                                                                        onClick={() =>
                                                                            toggleOrder(
                                                                                order.po_id
                                                                            )
                                                                        }
                                                                    >
                                                                        {expanded ? (
                                                                            <IconChevronUp
                                                                                size={
                                                                                    17
                                                                                }
                                                                            />
                                                                        ) : (
                                                                            <IconChevronDown
                                                                                size={
                                                                                    17
                                                                                }
                                                                            />
                                                                        )}
                                                                    </Button>
                                                                </Table.Td>

                                                                <Table.Td>
                                                                    <Text
                                                                        fw={
                                                                            700
                                                                        }
                                                                        size="sm"
                                                                    >
                                                                        {
                                                                            order.po_no
                                                                        }
                                                                    </Text>
                                                                </Table.Td>

                                                                <Table.Td>
                                                                    <Text
                                                                        size="sm"
                                                                    >
                                                                        {order.supplier_name ||
                                                                            "-"}
                                                                    </Text>
                                                                </Table.Td>

                                                                <Table.Td>
                                                                    <Badge
                                                                        variant="light"
                                                                        color="gray"
                                                                    >
                                                                        {
                                                                            order.style_count
                                                                        }
                                                                    </Badge>
                                                                </Table.Td>

                                                                <Table.Td>
                                                                    <Text
                                                                        size="sm"
                                                                    >
                                                                        {order.drivers?.length
                                                                            ? order.drivers.join(
                                                                                ", "
                                                                            )
                                                                            : "-"}
                                                                    </Text>
                                                                </Table.Td>

                                                                <Table.Td>
                                                                    <Text
                                                                        size="sm"
                                                                    >
                                                                        {order.floor_users?.length
                                                                            ? order.floor_users.join(
                                                                                ", "
                                                                            )
                                                                            : "-"}
                                                                    </Text>
                                                                </Table.Td>

                                                                <Table.Td>
                                                                    <Text
                                                                        size="sm"
                                                                    >
                                                                        {formatDateTime(
                                                                            order.first_pick_started_at
                                                                        )}
                                                                    </Text>
                                                                </Table.Td>

                                                                <Table.Td>
                                                                    <Text
                                                                        size="sm"
                                                                    >
                                                                        {formatDateTime(
                                                                            order.last_pick_completed_at
                                                                        )}
                                                                    </Text>
                                                                </Table.Td>

                                                                <Table.Td>
                                                                    <Text
                                                                        size="sm"
                                                                        fw={
                                                                            600
                                                                        }
                                                                    >
                                                                        {formatDateTime(
                                                                            order.last_activity_at
                                                                        )}
                                                                    </Text>
                                                                </Table.Td>
                                                            </Table.Tr>

                                                            <Table.Tr>
                                                                <Table.Td
                                                                    colSpan={
                                                                        9
                                                                    }
                                                                    className={
                                                                        classes.expandCell
                                                                    }
                                                                >
                                                                    <Collapse
                                                                        in={
                                                                            expanded
                                                                        }
                                                                    >
                                                                        {renderOrderStyles(
                                                                            order
                                                                        )}
                                                                    </Collapse>
                                                                </Table.Td>
                                                            </Table.Tr>
                                                        </React.Fragment>
                                                    );
                                                }
                                            )}
                                        </Table.Tbody>
                                    </Table>
                                </div>
                            )}

                            {(orderActivity
                                ?.pagination
                                .totalPages ??
                                0) > 1 && (
                                    <div
                                        className={
                                            classes.pagination
                                        }
                                    >
                                        <Pagination
                                            value={
                                                orderPage
                                            }
                                            onChange={
                                                setOrderPage
                                            }
                                            total={
                                                orderActivity
                                                    ?.pagination
                                                    .totalPages ??
                                                1
                                            }
                                        />
                                    </div>
                                )}
                        </section>

                        {/* =================================================
                            PEOPLE
                        ================================================= */}

                        <div
                            className={
                                classes.peopleGrid
                            }
                        >
                            {/* FORKLIFT USERS */}

                            <section
                                className={
                                    classes.peopleSection
                                }
                            >
                                <div
                                    className={
                                        classes.peopleHeader
                                    }
                                >
                                    <div>
                                        <Text
                                            fw={750}
                                            size="lg"
                                        >
                                            Forklift Drivers
                                        </Text>

                                        <Text
                                            size="xs"
                                            c="dimmed"
                                        >
                                            Click a driver for detailed
                                            statistics.
                                        </Text>
                                    </div>

                                    <Badge
                                        variant="light"
                                        color="gray"
                                    >
                                        {stats?.forklift.users.length ??
                                            0}
                                    </Badge>
                                </div>

                                <div
                                    className={
                                        classes.peopleList
                                    }
                                >
                                    {stats?.forklift.users.map(
                                        (user) => {
                                            const latest =
                                                findLatestForklift(
                                                    user._id
                                                );

                                            return (
                                                <button
                                                    type="button"
                                                    key={
                                                        user._id
                                                    }
                                                    className={
                                                        classes.personRow
                                                    }
                                                    onClick={() =>
                                                        openForkliftUser(
                                                            user
                                                        )
                                                    }
                                                >
                                                    <div
                                                        className={
                                                            classes.personIcon
                                                        }
                                                    >
                                                        <IconUser
                                                            size={
                                                                18
                                                            }
                                                        />
                                                    </div>

                                                    <div
                                                        className={
                                                            classes.personInfo
                                                        }
                                                    >
                                                        <Text
                                                            fw={
                                                                700
                                                            }
                                                            size="sm"
                                                        >
                                                            {
                                                                user.full_name
                                                            }
                                                        </Text>

                                                        {latest ? (
                                                            <div
                                                                className={
                                                                    classes.latestWork
                                                                }
                                                            >
                                                                <Text
                                                                    size="xs"
                                                                    c="dimmed"
                                                                >
                                                                    PO:{" "}
                                                                    <strong>
                                                                        {latest.po_no ||
                                                                            "-"}
                                                                    </strong>
                                                                </Text>

                                                                <Text
                                                                    size="xs"
                                                                    c="dimmed"
                                                                >
                                                                    Style:{" "}
                                                                    <strong>
                                                                        {latest.style_no ||
                                                                            "-"}
                                                                    </strong>
                                                                </Text>
                                                            </div>
                                                        ) : (
                                                            <Text
                                                                size="xs"
                                                                c="dimmed"
                                                            >
                                                                No work
                                                                recorded
                                                            </Text>
                                                        )}
                                                    </div>

                                                    <div
                                                        className={
                                                            classes.personRight
                                                        }
                                                    >
                                                        <Text
                                                            size="xs"
                                                            c="dimmed"
                                                        >
                                                            {formatDateTime(
                                                                latest?.pick_completed_at ||
                                                                latest?.pick_started_at
                                                            )}
                                                        </Text>
                                                    </div>
                                                </button>
                                            );
                                        }
                                    )}
                                </div>
                            </section>

                            {/* FLOOR USERS */}

                            <section
                                className={
                                    classes.peopleSection
                                }
                            >
                                <div
                                    className={
                                        classes.peopleHeader
                                    }
                                >
                                    <div>
                                        <Text
                                            fw={750}
                                            size="lg"
                                        >
                                            Floor Users
                                        </Text>

                                        <Text
                                            size="xs"
                                            c="dimmed"
                                        >
                                            Click a user for detailed
                                            allocation history.
                                        </Text>
                                    </div>

                                    <Badge
                                        variant="light"
                                        color="gray"
                                    >
                                        {stats?.floor.users.length ??
                                            0}
                                    </Badge>
                                </div>

                                <div
                                    className={
                                        classes.peopleList
                                    }
                                >
                                    {stats?.floor.users.map(
                                        (user) => {
                                            const latest =
                                                findLatestFloor(
                                                    user._id
                                                );

                                            return (
                                                <button
                                                    type="button"
                                                    key={
                                                        user._id
                                                    }
                                                    className={
                                                        classes.personRow
                                                    }
                                                    onClick={() =>
                                                        openFloorUser(
                                                            user
                                                        )
                                                    }
                                                >
                                                    <div
                                                        className={
                                                            classes.personIcon
                                                        }
                                                    >
                                                        <IconUser
                                                            size={
                                                                18
                                                            }
                                                        />
                                                    </div>

                                                    <div
                                                        className={
                                                            classes.personInfo
                                                        }
                                                    >
                                                        <Text
                                                            fw={
                                                                700
                                                            }
                                                            size="sm"
                                                        >
                                                            {
                                                                user.full_name
                                                            }
                                                        </Text>

                                                        {latest ? (
                                                            <div
                                                                className={
                                                                    classes.latestWork
                                                                }
                                                            >
                                                                <Text
                                                                    size="xs"
                                                                    c="dimmed"
                                                                >
                                                                    PO:{" "}
                                                                    <strong>
                                                                        {latest.po_no ||
                                                                            "-"}
                                                                    </strong>
                                                                </Text>

                                                                <Text
                                                                    size="xs"
                                                                    c="dimmed"
                                                                >
                                                                    Style:{" "}
                                                                    <strong>
                                                                        {latest.style_no ||
                                                                            "-"}
                                                                    </strong>
                                                                </Text>
                                                            </div>
                                                        ) : (
                                                            <Text
                                                                size="xs"
                                                                c="dimmed"
                                                            >
                                                                No work
                                                                recorded
                                                            </Text>
                                                        )}
                                                    </div>

                                                    <div
                                                        className={
                                                            classes.personRight
                                                        }
                                                    >
                                                        <Text
                                                            size="xs"
                                                            c="dimmed"
                                                        >
                                                            {formatDateTime(
                                                                latest?.updatedAt
                                                            )}
                                                        </Text>
                                                    </div>
                                                </button>
                                            );
                                        }
                                    )}
                                </div>
                            </section>
                        </div>
                    </>
                )}
            </main>

            {/* =================================================
                PERSON DETAIL MODAL
            ================================================= */}

            <Modal
                opened={
                    Boolean(
                        selectedPerson
                    )
                }
                onClose={
                    closePersonModal
                }
                title={
                    selectedPerson
                        ? `${selectedPerson.name} - ${selectedPerson.type ===
                            "forklift"
                            ? "Forklift Stats"
                            : "Floor Stats"
                        }`
                        : ""
                }
                size="95%"
                centered
            >
                {detailLoading &&
                    !forkliftDetail &&
                    !floorDetail ? (
                    <div
                        className={
                            classes.modalLoading
                        }
                    >
                        <Loader />
                    </div>
                ) : (
                    <>
                        <div
                            className={
                                classes.detailFilters
                            }
                        >
                            <TextInput
                                type="date"
                                label="Completed From"
                                value={
                                    completedFrom
                                }
                                onChange={(
                                    e
                                ) =>
                                    setCompletedFrom(
                                        e.currentTarget
                                            .value
                                    )
                                }
                            />

                            <TextInput
                                type="date"
                                label="Completed To"
                                value={
                                    completedTo
                                }
                                onChange={(
                                    e
                                ) =>
                                    setCompletedTo(
                                        e.currentTarget
                                            .value
                                    )
                                }
                            />

                            <Button
                                onClick={
                                    applyDetailFilters
                                }
                            >
                                Apply
                            </Button>

                            <Button
                                variant="default"
                                onClick={
                                    clearDetailFilters
                                }
                            >
                                Clear
                            </Button>
                        </div>

                        <Divider my="lg" />

                        {selectedPerson?.type ===
                            "forklift" &&
                            forkliftDetail && (
                                <>
                                    <div
                                        className={
                                            classes.detailSummaryGrid
                                        }
                                    >
                                        <Card withBorder>
                                            <Text
                                                size="xs"
                                                c="dimmed"
                                            >
                                                Today
                                            </Text>

                                            <Text
                                                className={
                                                    classes.detailValue
                                                }
                                            >
                                                {
                                                    forkliftDetail
                                                        .summary
                                                        .today_count
                                                }
                                            </Text>
                                        </Card>

                                        <Card withBorder>
                                            <Text
                                                size="xs"
                                                c="dimmed"
                                            >
                                                This Week
                                            </Text>

                                            <Text
                                                className={
                                                    classes.detailValue
                                                }
                                            >
                                                {
                                                    forkliftDetail
                                                        .summary
                                                        .week_count
                                                }
                                            </Text>
                                        </Card>

                                        <Card withBorder>
                                            <Text
                                                size="xs"
                                                c="dimmed"
                                            >
                                                This Month
                                            </Text>

                                            <Text
                                                className={
                                                    classes.detailValue
                                                }
                                            >
                                                {
                                                    forkliftDetail
                                                        .summary
                                                        .month_count
                                                }
                                            </Text>
                                        </Card>

                                        <Card withBorder>
                                            <Text
                                                size="xs"
                                                c="dimmed"
                                            >
                                                All Time
                                            </Text>

                                            <Text
                                                className={
                                                    classes.detailValue
                                                }
                                            >
                                                {
                                                    forkliftDetail
                                                        .summary
                                                        .all_time_count
                                                }
                                            </Text>
                                        </Card>
                                    </div>

                                    <div
                                        className={
                                            classes.averageGrid
                                        }
                                    >
                                        <Card withBorder>
                                            <Text
                                                size="xs"
                                                c="dimmed"
                                            >
                                                Weekly Avg
                                            </Text>

                                            <Text fw={700}>
                                                {formatMinutes(
                                                    forkliftDetail
                                                        .summary
                                                        .weekly_average_minutes
                                                )}
                                            </Text>
                                        </Card>

                                        <Card withBorder>
                                            <Text
                                                size="xs"
                                                c="dimmed"
                                            >
                                                Monthly Avg
                                            </Text>

                                            <Text fw={700}>
                                                {formatMinutes(
                                                    forkliftDetail
                                                        .summary
                                                        .monthly_average_minutes
                                                )}
                                            </Text>
                                        </Card>

                                        <Card withBorder>
                                            <Text
                                                size="xs"
                                                c="dimmed"
                                            >
                                                All Time Avg
                                            </Text>

                                            <Text fw={700}>
                                                {formatMinutes(
                                                    forkliftDetail
                                                        .summary
                                                        .all_time_average_minutes
                                                )}
                                            </Text>
                                        </Card>
                                    </div>

                                    <Group
                                        mt="md"
                                        mb="md"
                                    >
                                        <Badge
                                            color="green"
                                            size="lg"
                                        >
                                            Picked:{" "}
                                            {
                                                forkliftDetail
                                                    .summary
                                                    .picked_count
                                            }
                                        </Badge>

                                        <Badge
                                            color="green"
                                            variant="light"
                                            size="lg"
                                        >
                                            Picked All:{" "}
                                            {
                                                forkliftDetail
                                                    .summary
                                                    .picked_all_count
                                            }
                                        </Badge>

                                        <Badge
                                            color="red"
                                            variant="light"
                                            size="lg"
                                        >
                                            Not Found:{" "}
                                            {
                                                forkliftDetail
                                                    .summary
                                                    .not_found_count
                                            }
                                        </Badge>
                                    </Group>

                                    {renderForkliftDetailTable(
                                        forkliftDetail.allocations
                                    )}

                                    {forkliftDetail
                                        .pagination
                                        .totalPages >
                                        1 && (
                                            <div
                                                className={
                                                    classes.pagination
                                                }
                                            >
                                                <Pagination
                                                    value={
                                                        detailPage
                                                    }
                                                    onChange={
                                                        setDetailPage
                                                    }
                                                    total={
                                                        forkliftDetail
                                                            .pagination
                                                            .totalPages
                                                    }
                                                />
                                            </div>
                                        )}
                                </>
                            )}

                        {selectedPerson?.type ===
                            "floor" &&
                            floorDetail && (
                                <>
                                    <div
                                        className={
                                            classes.detailSummaryGrid
                                        }
                                    >
                                        <Card withBorder>
                                            <Text
                                                size="xs"
                                                c="dimmed"
                                            >
                                                Today
                                            </Text>

                                            <Text
                                                className={
                                                    classes.detailValue
                                                }
                                            >
                                                {
                                                    floorDetail
                                                        .summary
                                                        .today_count
                                                }
                                            </Text>
                                        </Card>

                                        <Card withBorder>
                                            <Text
                                                size="xs"
                                                c="dimmed"
                                            >
                                                This Week
                                            </Text>

                                            <Text
                                                className={
                                                    classes.detailValue
                                                }
                                            >
                                                {
                                                    floorDetail
                                                        .summary
                                                        .week_count
                                                }
                                            </Text>
                                        </Card>

                                        <Card withBorder>
                                            <Text
                                                size="xs"
                                                c="dimmed"
                                            >
                                                This Month
                                            </Text>

                                            <Text
                                                className={
                                                    classes.detailValue
                                                }
                                            >
                                                {
                                                    floorDetail
                                                        .summary
                                                        .month_count
                                                }
                                            </Text>
                                        </Card>

                                        <Card withBorder>
                                            <Text
                                                size="xs"
                                                c="dimmed"
                                            >
                                                All Time
                                            </Text>

                                            <Text
                                                className={
                                                    classes.detailValue
                                                }
                                            >
                                                {
                                                    floorDetail
                                                        .summary
                                                        .all_time_count
                                                }
                                            </Text>
                                        </Card>
                                    </div>

                                    <div
                                        className={
                                            classes.averageGrid
                                        }
                                    >
                                        <Card withBorder>
                                            <Text
                                                size="xs"
                                                c="dimmed"
                                            >
                                                Weekly Avg
                                            </Text>

                                            <Text fw={700}>
                                                {formatMinutes(
                                                    floorDetail
                                                        .summary
                                                        .weekly_average_minutes
                                                )}
                                            </Text>
                                        </Card>

                                        <Card withBorder>
                                            <Text
                                                size="xs"
                                                c="dimmed"
                                            >
                                                Monthly Avg
                                            </Text>

                                            <Text fw={700}>
                                                {formatMinutes(
                                                    floorDetail
                                                        .summary
                                                        .monthly_average_minutes
                                                )}
                                            </Text>
                                        </Card>

                                        <Card withBorder>
                                            <Text
                                                size="xs"
                                                c="dimmed"
                                            >
                                                All Time Avg
                                            </Text>

                                            <Text fw={700}>
                                                {formatMinutes(
                                                    floorDetail
                                                        .summary
                                                        .all_time_average_minutes
                                                )}
                                            </Text>
                                        </Card>

                                        <Card withBorder>
                                            <Text
                                                size="xs"
                                                c="dimmed"
                                            >
                                                Stores Completed
                                            </Text>

                                            <Text fw={700}>
                                                {
                                                    floorDetail
                                                        .summary
                                                        .stores_completed
                                                }
                                            </Text>
                                        </Card>
                                    </div>

                                    <div
                                        className={
                                            classes.detailTableTitle
                                        }
                                    >
                                        <Text fw={700}>
                                            Allocation History
                                        </Text>

                                        <Text
                                            size="sm"
                                            c="dimmed"
                                        >
                                            Floor time is estimated from
                                            consecutive save times.
                                        </Text>
                                    </div>

                                    {renderFloorDetailTable(
                                        floorDetail.allocations
                                    )}

                                    {floorDetail
                                        .pagination
                                        .totalPages >
                                        1 && (
                                            <div
                                                className={
                                                    classes.pagination
                                                }
                                            >
                                                <Pagination
                                                    value={
                                                        detailPage
                                                    }
                                                    onChange={
                                                        setDetailPage
                                                    }
                                                    total={
                                                        floorDetail
                                                            .pagination
                                                            .totalPages
                                                    }
                                                />
                                            </div>
                                        )}
                                </>
                            )}
                    </>
                )}
            </Modal>
        </div>
    );
};

export default ForkliftDashboard;